/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as mobx from "mobx";
import { ObservableMap } from "mobx";
import { IMemberEntity, ITagEntity, MemberStates } from "sh-models";
import { IMemberMapper } from "sh-models/member/mappers/IMemberMapper";
import { GraphService } from "sh-services/lib/graph/GraphService";

import { IScheduleGroupManager } from "./IScheduleGroupManager";
import { SearchMembersNotPartOfGroupParams } from "./SearchMembersNotPartOfGroupParams";
import { MemberShiftsDataGroup } from "../../staffhub/sh-application/components/schedule/ScheduleData";

/**
 * The schedule group manager.
 */
export class ScheduleGroupManager implements IScheduleGroupManager {
    /**
     * Creates a new instance of ScheduleGroupManager.
     * @param graphServiceClient The Graph service client.
     * @param memberMapper The member mappers.
     */
    constructor(private graphServiceClient: GraphService, private memberMapper: IMemberMapper) {}

    /**
     * Gets the people count for this schedule group.
     * @param tag The tag model aka schedule group.
     * @param filteredMembers The filtered members.
     * @returns The people count for this schedule group.
     */
    public getPeopleCount(
        tag: ITagEntity,
        filteredMembers: ObservableMap<string, IMemberEntity>,
        groupData?: MemberShiftsDataGroup
    ): number {
        let count = tag.memberIds.length;
        if (groupData) {
            // Remove the members that are deleted and don't have a shift
            const inactiveMembersWithNoShiftsCount = this.getInactiveMembersWithNoShiftsCount(tag, groupData);
            count -= inactiveMembersWithNoShiftsCount;
        }

        // Member filter will override the count
        if (filteredMembers.size > 0) {
            const tagMembersIdsSet = new Set(tag.memberIds);
            const filteredMembersIds = mobx.keys<string>(filteredMembers);

            count = filteredMembersIds.reduce((acc, memberId) => (tagMembersIdsSet.has(memberId) ? acc + 1 : acc), 0);
        }

        return count;
    }

    /**
     * Searches members which are not part of a given schedule group.
     * @param params The parameters.
     * @returns The list of members.
     */
    public async searchMembersNotPartOfGroup(params: SearchMembersNotPartOfGroupParams): Promise<IMemberEntity[]> {
        const { abortController, addedUsersIds, adminRoleId, availableMembersByUserIds, filters, groupId, ownersIds, teamId, tenantId } =
            params;

        const users = await this.graphServiceClient.searchGroupMembers({
            abortController,
            filters,
            groupId
        });

        const availableUsers = users.filter(user => !addedUsersIds.has(user.id));

        const availableMembers = users.reduce((acc, user) => {
            if (user.id in availableMembersByUserIds) {
                acc.push(availableMembersByUserIds[user.id]);
            }

            return acc;
        }, []);

        const members = this.memberMapper.mergeUsersProfilesWithMembersEntities({
            adminRoleId,
            members: availableMembers,
            ownersIds,
            teamId,
            tenantId,
            users: availableUsers
        });

        return members;
    }

    private getInactiveMembersWithNoShiftsCount(tag: ITagEntity, groupData: MemberShiftsDataGroup): number {
        const memberMap = new Map<string, IMemberEntity>();
        groupData.members.forEach(member => {
            memberMap.set(member.id, member);
        });
        let inactiveMembersWithNoShiftsCount = 0;
        tag.memberIds.forEach(memberId => {
            const memberShiftsData = groupData.memberShiftsData.get(memberId);
            if (memberShiftsData &&
                memberMap.get(memberId) &&
                (memberMap.get(memberId).state === MemberStates.AutoDeleted ||
                memberMap.get(memberId).state === MemberStates.Deleted) &&
                memberShiftsData.memberShifts.length == 0 &&
                memberShiftsData.memberTimeOffs.length == 0) {
                inactiveMembersWithNoShiftsCount++;
            }
        });
        return inactiveMembersWithNoShiftsCount;
    }
}
